// FireOpsPanel — bottom sheet for Fire Operations data layers.
//
// Opened from the fire-ops button (🔥) in MapPage. Provides independent
// toggles for:
//   • Active Fires (NIFC WFIGS large-fire incident locations)
//   • Fire TFRs (FAA TFRs rendered in the fire-ops context)
//
// Layout mirrors AeroSettingsSheet. Tapping the backdrop dismisses.
// v2 will add: dip sites (already in dipSites layer), helibase
// registry, frequency cheat sheets per incident.

import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import type { LayerId } from './layerCatalog';

interface Props {
  onClose: () => void;
}

interface ToggleRowProps {
  id: LayerId;
  label: string;
  sub?: string;
}

function ToggleRow({ id, label, sub }: ToggleRowProps) {
  const on          = useLayerVisibilityStore((s) => s.layers[id]);
  const toggleLayer = useLayerVisibilityStore((s) => s.toggleLayer);

  return (
    <div className="flex items-center justify-between py-2.5 border-b border-cockpit-700 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm text-cockpit-100 leading-tight">{label}</div>
        {sub && (
          <div className="text-[10px] text-cockpit-400 leading-tight mt-0.5">{sub}</div>
        )}
      </div>
      <button
        onClick={() => toggleLayer(id)}
        aria-pressed={on}
        className={[
          'relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ml-3',
          on ? 'bg-ember-600' : 'bg-cockpit-600',
        ].join(' ')}
        aria-label={`Toggle ${label}`}
      >
        <span
          className={[
            'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
            on ? 'translate-x-4' : 'translate-x-0',
          ].join(' ')}
        />
      </button>
    </div>
  );
}

export default function FireOpsPanel({ onClose }: Props) {
  return (
    // Full-screen backdrop for dismiss-on-outside-tap
    <div
      className="absolute inset-0 z-40"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="absolute bottom-0 left-0 right-0 bg-cockpit-900/97 backdrop-blur border-t border-cockpit-600 rounded-t-2xl shadow-2xl"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Fire Ops Layers"
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-2.5 pb-1">
          <span className="w-10 h-1 bg-cockpit-600 rounded-full" />
        </div>

        <div className="px-4 pb-1">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-base" aria-hidden>🔥</span>
              <h3 className="font-semibold text-cockpit-100 text-sm tracking-wide">Fire Ops</h3>
            </div>
            <button
              onClick={onClose}
              className="text-cockpit-300 text-xs hover:text-cockpit-100"
            >
              Done
            </button>
          </div>

          <div className="text-[10px] tracking-widest text-cockpit-400 mb-1">DATA LAYERS</div>

          <ToggleRow
            id="fireMissionIncidents"
            label="Active Fires"
            sub="NIFC WFIGS · ≥100 ac · updated 10 min"
          />
          <ToggleRow
            id="fireMissionTfrs"
            label="Fire TFRs"
            sub="FAA · all active TFRs · live · 5 min"
          />
        </div>

        <div className="px-4 pt-2">
          <p className="text-[10px] text-cockpit-400 leading-snug">
            Fire incident data from NIFC WFIGS. TFR data from FAA.
            Both are supplemental — verify with dispatch before entry.
          </p>
        </div>
      </div>
    </div>
  );
}
